import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import { DataTableContext } from "../Tabs/Overview";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import { ViewMasterCampaignsContext } from "../index";
import EditActivationPlatformModal from "../../Modals/EditActivationPlatformV2";
import MasterCampaignsStore from "../../../Flux/Stores/MasterCampaignsStore";
import BookFiltersStore from "../../../Flux/Stores/BookFiltersStore";
import { putFilterData } from "../../../Flux/Actions/BookFilterActions";
import { fetchActivationHeaders } from "../../../Flux/Actions/MasterCampaignActions";
import LoadingSpinner from "../../../Components/LoadingSpinner";

export default () => {
	// Define a ref that will be used to open and close the modal
	const dpRef = useRef();

	// Import various contexts
	const { checkboxSelections, dataProvider, dataTableApi } = useContext(DataTableContext);
	const { refreshData, platforms, permissionedDataKeys } = useContext(ViewMasterCampaignsContext);

	const [activationConfig, setActivationConfig] = useState();
	const [requestOpen, setRequestOpen] = useState();

	// Establish a listener to update the activation platform when fetchActivationHeaders completes
	useEffect(() => {
		const activationPlatformChanged = () => {
			setActivationConfig(MasterCampaignsStore.getActivationConfig());
		};
		MasterCampaignsStore.on("activationConfig", activationPlatformChanged);
		return () => {
			MasterCampaignsStore.removeListener("activationConfig", activationPlatformChanged);
		};
	}, []);

	const selectedMasterCampaign = useMemo(() => {
		const campaignInfo = checkboxSelections?.selectedActivationCampaigns?.[0];
		return dataProvider?.data?.find(({ MasterCampaignID }) => MasterCampaignID == campaignInfo?.masterCampaignId);
	}, [checkboxSelections, dataProvider]);

	// Property used to disable the Trigger button given specific criteria
	const disabled = useMemo(() => {
		return checkboxSelections?.selectedActivationCampaigns?.length != 1 || false;
	}, [checkboxSelections]);

	// Extract the actual selected campaign data from the overall data set
	const selectedCampaignData = useMemo(() => {
		if (requestOpen == true && dataProvider?.data && !disabled) {
			const campaignInfo = checkboxSelections?.selectedActivationCampaigns?.[0];
			const { activationCampaignId, masterCampaignId, platform } = campaignInfo;
			const masterCampaign = dataProvider?.data?.find(({ MasterCampaignID }) => MasterCampaignID == masterCampaignId);

			const activationCampaign = masterCampaign?.ActivationCampaigns?.[platform]?.find(({ CampaignID }) => CampaignID == activationCampaignId);

			return activationCampaign || null;
		}
		return null;
	}, [checkboxSelections, disabled, dataProvider, requestOpen]);

	// Request the activationHeaders, and when the request resolves open the modal
	useEffect(() => {
		if (selectedCampaignData && platforms) {
			// This sometimes throws an error when this effect cycles improperly. not sure why.
			try {
				putFilterData("platform", {
					value: selectedCampaignData?.MasterPlatformID,
					label: platforms?.find(({ MasterPlatformID }) => MasterPlatformID == selectedCampaignData?.MasterPlatformID)?.PlatformName
				});
			} catch (e) {}

			// SetLoading(true);

			fetchActivationHeaders(BookFiltersStore.getChoicesState(), selectedCampaignData?.HierarchyLevelID)
				.then(() => {
					// Open the modal now that we have all required information loaded
					setTimeout(() => {
						dpRef.current?.triggerOpenClose?.();
					}, 0);
				})
				.finally(() => {
					// SetLoading(false);
				});
		}
	}, [selectedCampaignData, platforms]);

	// Define a button that can be rendered wherever the client desires
	const Trigger = () => (
		<Button
			display={BUTTON_DISPLAY.TEXT}
			size={BUTTON_SIZE.SMALL}
			style={{ width: "fit-content" }}
			onClick={() => {
				setRequestOpen(true);
			}}
			disabled={disabled}
			id="editActivationCampaignButton"
			data-tracking-id-alt="edit-activation-campaign-button"
		>
			Edit Activation Campaign
		</Button>
	);

	// Define  the component that should be displayed when the trigger is clicked
	const DisplayedComponent = () => {
		if (activationConfig && selectedCampaignData) {
			return (
				<EditActivationPlatformModal
					editRef={(ref) => {
						dpRef.current = ref;
					}}
					config={activationConfig || {}}
					data={selectedCampaignData || []}
					masterCampaign={selectedMasterCampaign}
					permissionedDataKeys={permissionedDataKeys}
					// refresh={refreshData}
					refresh={() =>
						setTimeout(() => {
							dataTableApi?.current?.rowSelectionManager?.tree?.selectNode?.(false);
							dataProvider.update();
						}, 1)
					}
					onClose={() => {
						setRequestOpen(false);
						setActivationConfig(null);
					}}
					onSubmitComplete={() => {
						setRequestOpen(false);
						setActivationConfig(null);
					}}
				/>
			);
		}
		if (requestOpen) {
			return <LoadingSpinner />;
		}
		return <></>;
	};

	// Return a memoized object containing both of these components
	return useMemo(() => ({ Trigger, DisplayedComponent }), [dataProvider, refreshData, activationConfig, selectedCampaignData, disabled, platforms, requestOpen]);
};
